import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { format } from "date-fns";
import { ja } from "date-fns/locale";

import useAppSelector from "hooks/useAppSelector";
import useAllClasses from "hooks/useAllClasses";
import useAppDispatch from "hooks/useAppDispatch";
import MessageDialog from "components/__reusable/MessageDialog";
import DateInput from "components/__reusable/DateInput";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import Checkbox from "components/__reusable/Checkbox";
import ParentsRegistrationLetters from "assets/lettersTemplate/ParentsRegistrationLetters";
import generatePdf from "utilities/PdfGenerator";
import { setErrorStatus } from "features/error/errorSlice";
import type { Student } from "types/Student";
import PlanSelectionGuideImage from "assets/lettersTemplate/PlanSelectionGuide.png";

const DEFAULT_DATE = new Date();
DEFAULT_DATE.setMonth(DEFAULT_DATE.getMonth() + 1);
DEFAULT_DATE.setHours(23, 59, 59, 999);

const MIN_DATE = new Date();
MIN_DATE.setDate(MIN_DATE.getDate() + 1);

interface RegistrationDocumentDeadlineDialogProps {
  onYesClick: () => void;
  onNoClick: () => void;
}

interface StudentRegistrationData {
  student: Student;
  isSelected: boolean;
  isEligible: boolean; // Can be selected (no guardian registration)
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const RegistrationDocumentDeadlineDialog: React.FC<
  RegistrationDocumentDeadlineDialogProps
> = ({ onNoClick, onYesClick }) => {
  const { t } = useTranslation();
  const { students } = useAppSelector((state) => state.student);
  const { classes } = useAllClasses();
  const dispatch = useAppDispatch();

  const [expiredDate, setExpiredDate] = useState(DEFAULT_DATE);
  const [studentData, setStudentData] = useState<StudentRegistrationData[]>([]);
  const [showDeadlineModal, setShowDeadlineModal] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [printPlanNotice, setPrintPlanNotice] = useState(true);

  useEffect(() => {
    if (students.length > 0) {
      const initialData = students.map((student) => {
        // Check if student has guardian registration
        const hasRegistration = !!student.parent;

        return {
          student,
          isSelected: false,
          isEligible: !hasRegistration, // Only students without registration are eligible
        };
      });

      setStudentData(initialData);
    }
  }, [students]);

  // Check if any eligible students are selected
  const hasSelectedStudents = studentData.some(
    (data) => data.isSelected && data.isEligible,
  );

  const eligibleStudents = studentData.filter((data) => data.isEligible);
  const areAllEligibleSelected =
    eligibleStudents.length > 0 &&
    eligibleStudents.every((data) => data.isSelected);

  // Handle student selection
  const handleStudentSelect = useCallback(
    (studentId: number, isSelected: boolean) => {
      setStudentData((prev) =>
        prev.map((data) =>
          data.student.id === studentId && data.isEligible
            ? { ...data, isSelected }
            : data,
        ),
      );
    },
    [],
  );

  const handleToggleSelectAll = useCallback((forceValue?: boolean) => {
    setStudentData((prev) => {
      const eligible = prev.filter((data) => data.isEligible);
      const shouldSelectAll =
        forceValue ??
        (eligible.length === 0 || !eligible.every((data) => data.isSelected));

      return prev.map((data) =>
        data.isEligible ? { ...data, isSelected: shouldSelectAll } : data,
      );
    });
  }, []);

  // Handle deadline setting
  const handleDeadlineSet = () => {
    setShowDeadlineModal(false);
  };

  // Issue registration documents
  const issueDocument = async () => {
    const selectedStudents = studentData
      .filter((data) => data.isSelected && data.isEligible)
      .map((data) => data.student);

    if (selectedStudents.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      let content = "";
      const expiryDateString = `${expiredDate.getFullYear()}-${(
        expiredDate.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${expiredDate
          .getDate()
          .toString()
          .padStart(2, "0")}`;

      const pageBreakStyle = `
        <style>
          .pdf-page { page-break-after: always; }
          .pdf-page:last-child { page-break-after: auto; }
        </style>
      `;
      const planContent = printPlanNotice
        ? `<div class="print-container" style="margin: 60px auto; max-width: 960px;">
            <img
              src="${PlanSelectionGuideImage}"
              alt="保護者プラン案内"
              style="
                width: 100%;
                height: auto;
                display: block;
                background: white;
              "
            />
          </div>`
        : "";

      const pages: string[] = [];
      for (let index = 0; index < selectedStudents.length; index++) {
        const student = selectedStudents[index];
        const studentName = escapeHtml(
          `${student.last_name} ${student.first_name}`,
        );
        const studentClass = classes.find(
          (classItem) => classItem.id === student.class_id,
        );
        const classNameWithYear = studentClass
          ? `${studentClass.year}年${studentClass.name}`
          : "";
        const letterTitle = classNameWithYear
          ? `${escapeHtml(classNameWithYear)}　${studentName}さん用`
          : `${studentName}さん用`;
        const studentActivateCode = JSON.stringify({
          activate_code: [student.activate_code],
          expiry_date: expiryDateString,
        });
        let qrCode = "";
        try {
          qrCode = await QRCode.toDataURL(studentActivateCode);
        } catch (error) {
          console.error("Error generating QR code:", error);
        }
        let htmlBody = ParentsRegistrationLetters.replaceAll(
          "{{title}}",
          letterTitle,
        )
          .replaceAll("{{student_name}}", studentName)
          .replaceAll("{{qrcode}}", qrCode)
          .replaceAll(
            "{{date}}",
            format(expiredDate, "yyyy年MM月dd日（EEE）", { locale: ja }),
          );
        pages.push(`<div class="pdf-page">${htmlBody}</div>`);
        if (planContent) {
          pages.push(`<div class="pdf-page">${planContent}</div>`);
        }
      }

      content = pageBreakStyle + pages.join("");
      generatePdf("保護者アプリ初回登録のご案内", "portrait", content);

      onYesClick();
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "登録用紙の発行に失敗しました",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderStudentName = (student: Student) => {
    if (student.first_name_kata && student.last_name_kata) {
      return `${student.last_name} ${student.first_name} (${student.last_name_kata} ${student.first_name_kata})`;
    }
    return `${student.last_name} ${student.first_name}`;
  };

  const formatRegistrationDate = (student: Student) => {
    if (student.parent?.create_time) {
      const date = new Date(student.parent.create_time);
      return format(date, "yyyy年MM月dd日", { locale: ja });
    }
    return "!"; // Alert indicator for missing registration
  };

  // Show deadline setting modal first
  if (showDeadlineModal) {
    return (
      <MessageDialog>
        <div className="space-y-3">
          <p>{t("_student._registration_deadline")}</p>
          <div className="space-y-2">
            <DateInput
              name={"expiredDate"}
              currentDate={expiredDate}
              minDate={MIN_DATE}
              onChange={(value) => setExpiredDate(value)}
            />
            <div className="flex justify-end">
              <Checkbox
                name="print-plan-notice"
                label="プラン選択案内も印刷する"
                isChecked={printPlanNotice}
                onChange={(checked) => setPrintPlanNotice(checked)}
              />
            </div>
          </div>
          <div className="flex justify-between items-center space-x-5 w-full">
            <div className="w-1/2">
              <SecondaryActionButton
                label={t("_generic._cancel")}
                onClick={onNoClick}
              />
            </div>
            <div className="w-1/2">
              <PrimaryActionButton
                label={t("_student._issue")}
                disabled={expiredDate < MIN_DATE}
                onClick={handleDeadlineSet}
              />
            </div>
          </div>
        </div>
      </MessageDialog>
    );
  }

  // Show main registration form dialog
  return (
    <MessageDialog>
      <div className="h-[75vh] flex flex-col space-y-3">
        {/* Title with green background */}
        <div className="bg-[#00A09B] p-3 rounded-t-lg">
          <h3 className="text-xl font-bold text-center text-white">
            登録用紙を発行する
          </h3>
        </div>

        {/* Subtitle */}
        <div className="px-3">
          <p className="text-sm text-gray-600">
            選択した園児の登録用紙を発行します。
          </p>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto relative">
          <div className="w-full h-full absolute">
            <table className="w-full relative">
              {/* Fixed header */}
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="w-[10%] p-2 bg-[#f7f7f7] border border-[#D9D9D9]">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        name="select-all-students"
                        label="すべて選択"
                        labelClassName="sr-only"
                        isChecked={areAllEligibleSelected}
                        disabled={eligibleStudents.length === 0}
                        onChange={(isSelected) =>
                          handleToggleSelectAll(isSelected)
                        }
                      />
                    </div>
                  </th>
                  <th className="w-[50%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    園児名
                  </th>
                  <th className="w-[40%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    保護者登録日
                  </th>
                </tr>
              </thead>
              <tbody>
                {studentData.map((data) => (
                  <tr
                    key={data.student.id}
                    className={!data.isEligible ? "bg-gray-100 opacity-50" : ""}
                  >
                    <td className="w-[10%] p-2 border border-[#D9D9D9] text-center">
                      <Checkbox
                        name={`student-registration-${data.student.id}`}
                        label=""
                        isChecked={data.isSelected}
                        disabled={!data.isEligible}
                        onChange={(isSelected) =>
                          handleStudentSelect(data.student.id, isSelected)
                        }
                        aria-label={`${renderStudentName(data.student)}を選択`}
                      />
                    </td>
                    <td className="w-[50%] p-2 border border-[#D9D9D9]">
                      <div className="flex items-center space-x-3">
                        <ProfileAvatar
                          pictureUrl={
                            data.student.profile_picture_for_admin || ""
                          }
                        />
                        <span className="text-sm">
                          {renderStudentName(data.student)}
                        </span>
                      </div>
                    </td>
                    <td className="w-[40%] p-2 border border-[#D9D9D9]">
                      <div className="flex items-center justify-center">
                        {!data.isEligible ? (
                          <span className="text-sm text-gray-600">
                            {formatRegistrationDate(data.student)}
                          </span>
                        ) : (
                          <span className="text-red-500 font-bold text-lg">
                            !
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {studentData.length === 0 && (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">データがありません</p>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-between items-center space-x-5 pt-3">
          <div className="w-1/2">
            <SecondaryActionButton
              label={t("_generic._cancel")}
              onClick={onNoClick}
            />
          </div>
          <div className="w-1/2">
            <PrimaryActionButton
              label="登録用紙を発行する"
              loading={isLoading}
              disabled={!hasSelectedStudents}
              onClick={issueDocument}
            />
          </div>
        </div>
      </div>
    </MessageDialog>
  );
};

export default RegistrationDocumentDeadlineDialog;
